/**
 * Utilities for the Users custom dashboard widget family.
 * ENG-130634.
 */
import { buildUrl } from 'components/InternalLink';
import _ from 'lodash';
import type { UserStatsApiFilters } from 'types/api/user';
import type { FilterInfo } from 'types/pagination';
import type { UserBasicFilters } from 'types/userFilters';
import { getStartAndEndDateForQuickOption } from 'utils/date/quickOptions';
import { getUrlSearch } from 'utils/urlUtil';

/**
 * Converts UserBasicFilters (UI/config keys) to UserStatsApiFilters (API payload).
 *
 * Transformations:
 *   - Empty arrays dropped (pickBy)
 *   - name → searchName, email → searchEmail
 *   - disableLogin boolean[] passed through unchanged
 *   - lastlogin quick-option label → [startISO, endISO], renamed to lastLogin
 *
 * Use this wherever UserBasicFilters needs to be sent to the stats API.
 */
export function toUserStatsApiFilters(
  basicFilters: UserBasicFilters | undefined,
): UserStatsApiFilters {
  const picked = _.pickBy(
    basicFilters,
    (v) => Array.isArray(v) && (v as unknown[]).length > 0,
  ) as Partial<UserBasicFilters>;

  let lastLogin: string[] | undefined;
  if (picked.lastlogin?.length) {
    lastLogin =
      picked.lastlogin.length === 1
        ? (getStartAndEndDateForQuickOption({
            label: picked.lastlogin[0],
            utcString: true,
          }) as string[])
        : picked.lastlogin;
  }

  return {
    ...(picked.team ? { team: picked.team } : {}),
    ...(picked.role ? { role: picked.role } : {}),
    ...(picked.name ? { searchName: picked.name } : {}),
    ...(picked.email ? { searchEmail: picked.email } : {}),
    ...(picked.disableLogin ? { disableLogin: picked.disableLogin } : {}),
    ...(lastLogin ? { lastLogin } : {}),
  };
}

/**
 * Builds a deep-link URL to the User Management page pre-seeded with the
 * widget's basicFilters. Group-by is intentionally omitted — the User
 * Management page does not support a group-by URL param.
 */
export function buildUsersDeepLinkUrl(filters: UserBasicFilters): string {
  const basePath = buildUrl('/user-management/users');
  const qs = getUrlSearch({ filters: filters as unknown as FilterInfo }, '');
  return `/#${basePath}?${qs}`;
}
